import React, { useEffect, useRef, useState, useTransition, useCallback } from "react";
import Card from "react-bootstrap/Card";
import ReactCountryFlag from "react-country-flag";
import { useDispatch, useSelector } from "react-redux";
import commaFormat from "@utils/commaFormat";
import { formatStringCase, useDebounce } from "@utils/utils";
import ViewAllFilter from "./viewAll-filter.jsx";
import IncludeExcludeFilter from "./includeExcludeFilter.jsx";
import LoaderImg from "@assets/images/loader.gif";
import { selectedFilterListHandler } from "@redux/Shipment/shipmentSlice.jsx";
import Select from "react-select";
import { MinusCircle, PlusCircle } from "react-feather";
import EmptyFilter from "./empty-filter.jsx";

const categoryStyles = {
  control: (base) => ({
    ...base,
    height: 36,
    minHeight: 36,
    backgroundColor: "#fafafa",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: 500,
  }),
  menu: (base) => ({
    ...base,
    fontSize: "14px",
    fontWeight: 500,
    padding: "8px",
  }),
  option: (base, { isDisabled, isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isDisabled ? null : isSelected ? "rgba(111, 8, 241, 0.1)" : isFocused ? "rgba(111, 8, 241, 0.1)" : null,
    color: isDisabled ? null : isSelected ? "#1a1722" : isFocused ? "#1a1722" : null,
    cursor: "pointer !important",
    marginBottom: "8px",
  }),
};

const LIMIT = 50;
const FETCH_THRESHOLD = 30;

const ViewAll = ({ placeholder, noDataFound, setNoDataFound, onApply, closeFilter, filterList, emptyContent, filterKeyword, heading, showInput, label, isCapitaliseInViewAll, includeFilter, excludeFilter, showIncludeExclude, isTitleCase, appliedFilterList, isParanthesesCapital = false, isNoTransform = false, getFilterDataHandler, filterDetails, hasMore, loader, searchedData, additionalFilterList, onSelect, additionalAppliedFilterList }) => {
  const [isPending, startTransition] = useTransition();
  const [includeFilterButton, setIncludeFilterButton] = useState("true");
  const [filterData, setFilterData] = useState([]);
  const [filterListClone, setFilterListClone] = useState([]);
  const [inputValue, setInputValue] = useState(null);
  const debouncedInputValue = useDebounce(inputValue, 500);
  const [isLoaderActive, setIsLoaderActive] = useState(false);
  const [selectedFilterIds, setSelectedFilterIds] = useState([]);
  const [selectedFilterList, setSelectedFilterList] = useState({});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const filterLoader = useSelector((state) => state.shipmentDetails.filterLoader);
  const mustNot = useSelector((state) => state.shipmentDetails.filterMustNot);
  const inputRef = useRef(null);
  const observer = useRef();
  const [displayedItems, setDisplayedItems] = useState(LIMIT);
  const [cachedData, setCachedData] = useState([]);
  const [filterListDocCount, setFilterListDocCount] = useState({});
  const [perUnitFilterData, setPerUnitFilterData] = useState([]);
  const [rows, setRows] = useState([{ id: 1, dropdown: null, input1: null, input2: null }]);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [error, setError] = useState({});
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const dispatch = useDispatch();
  const appliedFilterListNew = useSelector((state) => state.shipmentDetails.selectedFilterList);

  const [isPerUnit, setIsPerUnit] = useState(additionalAppliedFilterList?.length > 0 ? true : false);

  useEffect(() => {
    if (!mustNot || mustNot?.length <= 1000) {
      initializeFilterData();
    } else {
      const selectedDataList = searchedData?.length > 0 || inputValue?.length > 0 ? searchedData : filterList;
      const tempDocCount = filterListDocCount;
      setIsSelectAllChecked(checkIfAllSelected(selectedDataList));
      selectedDataList.forEach((item) => (tempDocCount[item.key] = item.doc_count));
      setFilterListDocCount(tempDocCount);
      setCachedData(selectedDataList);
    }
  }, [filterList, mustNot, searchedData]);

  useEffect(() => {
    setSelectedFilterIds([...appliedFilterList]);
    setSelectedFilterList(appliedFilterListNew?.[filterDetails.key] ?? []);
  }, [appliedFilterList]);

  useEffect(() => {
    initializeIncludeExcludeFilter();
    setNoDataFound(false);
  }, []);

  useEffect(() => {
    if (inputValue !== null) handleSearch(debouncedInputValue);
    setNoDataFound(false);
  }, [debouncedInputValue]);

  const initializeFilterData = () => {
    setIsLoaderActive(true);
    const selectedDataList = searchedData?.length > 0 || inputValue?.length > 0 ? searchedData : filterList;
    const tempDocCount = filterListDocCount;
    setIsSelectAllChecked(checkIfAllSelected(selectedDataList));
    selectedDataList.forEach((item) => (tempDocCount[item.key] = item.doc_count));
    setFilterListDocCount(tempDocCount);
    const searchedResultArr = selectedDataList.filter((item) => (item.description && item.description) || (item.key && item.key.toLowerCase()));
    const sortedSearchedResultArr = sortSearchResults(searchedResultArr);
    setFilterData([...sortedSearchedResultArr.slice(0, LIMIT)]);
    setFilterListClone(sortedSearchedResultArr ? sortedSearchedResultArr.slice(0, LIMIT) : []);
    setCachedData(sortedSearchedResultArr || []);
    setIsLoaderActive(false);
  };

  const initializeIncludeExcludeFilter = () => {
    setIsLoaderActive(true);
    if (includeFilter.includes(filterKeyword)) {
      setIncludeFilterButton("true");
    } else if (excludeFilter.includes(filterKeyword)) {
      setIncludeFilterButton("false");
    }
    const selectedDataList = searchedData?.length > 0 || inputValue?.length > 0 ? searchedData : filterList;
    setFilterData(JSON.parse(JSON.stringify(selectedDataList.slice(0, LIMIT))));
    setIsLoaderActive(false);
  };

  const changeIncludeExcludeSelection = (data) => {
    setIncludeFilterButton(data);
  };

  const handleSearch = async (text) => {
    setIsLoaderActive(true);
    setFilterData([]);
    await getFilterDataHandler(filterDetails, undefined, text);
    setDisplayedItems(LIMIT);
    setIsLoaderActive(false);
  };

  const sortSearchResults = (results) => {
    const selectedSet = new Set(selectedFilterIds);
    let uniqueResults = [];
    if (inputValue !== "" && inputValue !== null) {
      uniqueResults = results.sort((a, b) => {
        return selectedSet.has(b.key) - selectedSet.has(a.key);
      });
    } else {
      results = [...results.filter((item) => !selectedFilterList[item.key])];
      uniqueResults = [...new Set([...Object.values(selectedFilterList), ...results])].sort((a, b) => {
        return selectedSet.has(b.key) - selectedSet.has(a.key);
      });
    }
    return uniqueResults;
  };

  const checkIfAllSelected = (results) => {
    const selectedSet = new Set(selectedFilterIds);
    return results?.every((item) => selectedSet.has(item?.key));
  };

  const handleOnSelect = (isChecked, data) => {
    const newSelectedIds = new Set(selectedFilterIds);
    const newSelectedFilterList = JSON.parse(JSON.stringify(selectedFilterList));
    if (isChecked) {
      newSelectedIds.add(data.key);
      newSelectedFilterList[data.key] = data;
    } else {
      newSelectedIds.delete(data.key);
      delete newSelectedFilterList[data.key];
    }
    setIsSelectAllChecked(filterData.every((item) => newSelectedIds.has(item.key)));
    setFiltersStateHandler(newSelectedIds);
    setSelectedFilterIds([...newSelectedIds]);
    setSelectedFilterList({ ...newSelectedFilterList });
  };

  const handleSelectAll = () => {
    const selectedDataList = searchedData.length > 0 || inputValue?.length > 0 ? searchedData : filterList;
    const tempSearchData = JSON.parse(JSON.stringify(selectedDataList));
    const newSelectedFilterList = JSON.parse(JSON.stringify(selectedFilterList));
    const newSelectedIds = new Set(selectedFilterIds);
    if (isSelectAllChecked) {
      tempSearchData.forEach((item) => newSelectedIds.delete(item.key));
      tempSearchData.forEach((item) => delete newSelectedFilterList[item.key]);
    } else {
      tempSearchData.forEach((item) => newSelectedIds.add(item.key));
      tempSearchData.forEach((item) => (newSelectedFilterList[item.key] = item));
    }
    setIsSelectAllChecked(!isSelectAllChecked);
    setSelectedFilterIds([...newSelectedIds]);
    setFiltersStateHandler(newSelectedIds);
    setSelectedFilterList({ ...newSelectedFilterList });
  };

  const setFiltersStateHandler = (selectedFilterIds) => {
    startTransition(() => {
      const updatedFilterListClone = filterListClone.map((item) => ({
        ...item,
        isChecked: selectedFilterIds.has(item.key),
      }));
      setFilterListClone(updatedFilterListClone);
    });
  };

  const loadMore = useCallback(() => {
    startTransition(() => {
      const newDisplayedItems = displayedItems + LIMIT;
      setDisplayedItems(newDisplayedItems);
      setFilterData(cachedData.slice(0, newDisplayedItems));
      setFilterListClone(cachedData.slice(0, newDisplayedItems));

      if (newDisplayedItems + FETCH_THRESHOLD >= cachedData.length && hasMore) {
        getFilterDataHandler(filterDetails, mustNot, inputValue);
      }
    });
  }, [displayedItems, cachedData, hasMore, inputValue]);

  const lastElementRef = useCallback(
    (node) => {
      if (loader) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && cachedData.length > displayedItems) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loader, loadMore, cachedData, displayedItems, inputValue]
  );

  useEffect(() => {
    setPerUnitFilterData(additionalFilterList || []);
  }, [perUnitFilterData, isPerUnit]);
  useEffect(() => {
    let initialOptions = [];
    for (let i = 0; i < additionalFilterList?.length; i++) {
      initialOptions.push({
        label: additionalFilterList[i].key,
        value: additionalFilterList[i].key,
        range: additionalFilterList[i].range,
      });
    }
    setAvailableOptions(initialOptions);
    if (additionalAppliedFilterList && additionalAppliedFilterList?.length > 0) {
      let tempRows = [];
      let availableOptionsClone = JSON.parse(JSON.stringify(initialOptions));
      for (let i = 0; i < additionalAppliedFilterList?.length; i++) {
        let tempAppliedFilter = additionalAppliedFilterList[i];
        availableOptionsClone = availableOptionsClone?.filter((item) => item.value !== tempAppliedFilter.key);
        tempRows.push({
          id: i + 1,
          input1: tempAppliedFilter?.range?.[0],
          input2: tempAppliedFilter?.range?.[1],
          dropdown: initialOptions?.find((item) => item.value === tempAppliedFilter?.key),
        });
      }
      setAvailableOptions(availableOptionsClone);
      setRows(tempRows);
    }
  }, [additionalFilterList, isPerUnit]);
  const handleChange = (id, field, value, minMaxValue) => {
    let tempError = { ...error };
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === id) {
          if (field === "dropdown") {
            setAvailableOptions((prev) => {
              const newOptions = [...prev];
              if (row.dropdown) {
                newOptions.push(row.dropdown);
              }
              return newOptions.filter((option) => option.value !== value.value);
            });
            row.input1 = null;
            row.input2 = null;
          }
          if (field === "input1") {
            if (value < minMaxValue.range[0] || value === "") {
              tempError[row.id] = "Input Value Cannot be less than " + minMaxValue.range[0];
            } else if (row.input2 > row.dropdown.range[1]) {
              tempError[row.id] = "Input Value Cannot be greater than " + row.dropdown.range[1];
            } else tempError[row.id] = "";
          } else if (field === "input2") {
            if (value > minMaxValue.range[1] || value === "") {
              tempError[row.id] = "Input Value Cannot be greater than " + minMaxValue.range[1];
            } else if (row.input1 < row.dropdown.range[0]) {
              tempError[row.id] = "Input Value Cannot be less than " + row.dropdown.range[0];
            } else tempError[row.id] = "";
          }
          setError(tempError);
          return { ...row, [field]: value };
        }
        return row;
      })
    );
  };

  const addRow = () => {
    const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
    setRows([...rows, { id: newId, dropdown: null, input1: null, input2: null }]);
  };

  const removeRow = (id) => {
    setRows((prevRows) => {
      const removedRow = prevRows.find((row) => row.id === id);
      if (removedRow && removedRow.dropdown) {
        setAvailableOptions((prev) => [...prev, removedRow.dropdown]);
      }
      return prevRows.filter((row) => row.id !== id);
    });
  };

  const checkError = (row) => {
    if (row.input2 && row.input1 && +row.input2 < +row.input1) {
      return true;
    }
    if (row.input2 === null && row.input1 !== null && row.dropdown && +row.dropdown.range[1] < +row.input1) {
      return true;
    }
    if (row.input1 === null && row.input2 !== null && row.dropdown && +row.dropdown.range[0] > +row.input2) {
      return true;
    }
    if (row.input1 === null && row.input2 === null && row.dropdown && +row.dropdown.range[0] > +row.dropdown.range[1]) {
      return true;
    }
    return false;
  };

  const checkDisable = () => {
    if (rows?.length > 0) {
      for (let i = 0; i < rows?.length; i++) {
        if (checkError(rows[i]) || !Object.keys(error).every((key) => error[key] === "") || rows.every((row) => row.dropdown === null)) {
          return true;
        }
      }
      return false;
    } else return true;
  };
  const handleApply = () => {
    if (isPerUnit) {
      onSelect(rows, "true");
    } else {
      onApply(includeFilterButton, filterListClone, selectedFilterIds);
      let tempAppliedSelectedList = JSON.parse(JSON.stringify(appliedFilterListNew));
      tempAppliedSelectedList[filterDetails.key] = selectedFilterList;
      dispatch(selectedFilterListHandler(tempAppliedSelectedList));
    }
  };
  const handleApplyClick = () => {
    if (isPerUnit) {
      if (selectedFilterIds.length > 0) {
        setConfirmationPopup(true);
      } else {
        handleApply();
      }
    } else {
      if (!checkDisable()) {
        setConfirmationPopup(true);
      } else {
        handleApply();
      }
    }
  };
  return (
    <Card className={confirmationPopup ? "view-all-filter-panel-card confirmation" : "view-all-filter-panel-card"} id="proximity-search-viewall" onClick={(e) => e.stopPropagation()}>
      {confirmationPopup ? (
        <Card.Header>
          <h6 className="mb-0 p-2">Confirmation</h6>
        </Card.Header>
      ) : (
        <Card.Header>
          <div className="d-flex">
            <div className="col-md-5 pl-0">
              {filterDetails?.additionalType === "multiSelectRangeFilter" ? (
                <span id="proximity-switch-button" className="ml-2">
                  <button className={isPerUnit ? "proximity-button mr-1" : "proximity-button-active mr-1"} disabled={filterLoader || isLoaderActive} onClick={() => setIsPerUnit(false)}>
                    Quantity Units
                  </button>
                  <button className={!isPerUnit ? "proximity-button" : "proximity-button-active"} disabled={filterLoader || isLoaderActive} onClick={() => setIsPerUnit(true)}>
                    Quantity Value
                  </button>
                </span>
              ) : (
                <h5 className="modal_header ml-3">{heading ? heading : placeholder}</h5>
              )}
            </div>
            <div className="col-md-3 pl-0" />
            <div className="col-md-4 text-right pr-0 d-flex justify-content-end">
              <button type="button" disabled={checkDisable() && selectedFilterIds.length === 0} className="btn btn-primary btn-sm trademo-new-btn" onClick={() => handleApplyClick()}>
                Apply Filter
              </button>
              <button type="button" className="close trademo-modal-close" aria-label="Close" onClick={() => closeFilter(filterKeyword)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
        </Card.Header>
      )}
      {confirmationPopup ? (
        <div className="row m-0 p-4 label-md-regular ">This change will override the previously selected {isPerUnit ? "Quantity Values" : "Quantity Units"}. Click on confirm to continue.</div>
      ) : (
        <Card.Body className="pt-0">
          {isPerUnit ? (
            <>
              {filterLoader ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "calc(100% - 146px)" }}>
                  <img src={LoaderImg} alt="description of gif" />
                </div>
              ) : filterData?.length !== 0 ? (
                <div>
                  {rows.map((row, index) => (
                    <div key={row.id} className="d-flex flex-wrap align-items-center range-view-all-card h-100 my-3">
                      <div className="col-md-4 d-flex align-items-center text-no-transform">
                        <div className="label-sm-regular text-no-transform text-nowrap mr-3">Select quantity unit</div>
                        <Select className="match-type w-100" placeholder="Select Quantity" options={availableOptions} styles={categoryStyles} onChange={(value) => handleChange(row.id, "dropdown", value)} value={row.dropdown} />
                      </div>
                      <div className="col-md-3">
                        <div className="px-0 d-flex align-items-center">
                          <div className="range-label text-nowrap mr-3">Minimum range</div>
                          <input className="box label-styling range-input" value={row?.input1 !== null ? row?.input1 : row?.dropdown?.range?.[0]} disabled={row.dropdown === null} type="number" onChange={(e) => handleChange(row.id, "input1", e.target.value, row.dropdown)} />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="px-0 d-flex align-items-center">
                          <div className="range-label text-nowrap mr-3">Maximum range</div>
                          <input className="box label-styling range-input" value={row?.input2 !== null ? row?.input2 : row?.dropdown?.range?.[1]} disabled={row.dropdown === null} type="number" onChange={(e) => handleChange(row.id, "input2", e.target.value, row.dropdown)} />
                        </div>
                      </div>
                      <div className="col-md-1 d-flex align-items-center justify-content-start">
                        <div>{((rows?.length > 1 && index !== rows?.length - 1) || (index > 0 && row?.dropdown !== null)) && <MinusCircle size={24} color="#667085" onClick={() => removeRow(row.id)} className="cursor-pointer" />}</div>
                        <div>{!(rows?.length > 1 && index !== rows?.length - 1) && <PlusCircle size={24} color="#667085" onClick={addRow} className="cursor-pointer" />}</div>
                      </div>
                      {error[row.id] && <div className="text-danger my-2 label-sm-medium text-capitalize col-md-12">{error[row.id]}</div>}
                      {checkError(row) ? <div className="text-danger my-2 label-sm-medium text-capitalize col-md-12">Maximum range should be greater than minimum range</div> : ""}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyFilter content={emptyContent ? emptyContent : ""} />
              )}{" "}
            </>
          ) : (
            <>
              <div className={`${showInput === false ? "" : "view-all-border-bottom"} mb-3 viewall-sticky-header pt-2`}>
                {showIncludeExclude ? <IncludeExcludeFilter includeFilter={includeFilter.includes(filterKeyword)} excludeFilter={excludeFilter.includes(filterKeyword)} setIncludeFilter={(data) => changeIncludeExcludeSelection(data)} /> : null}
                {!!filterData && filterData.length !== 0 && showInput === false ? (
                  ""
                ) : (
                  <div className="col-md-5 pl-0 mt-2 mb-4 pl-3 ">
                    <input
                      className="form-control"
                      type="text"
                      ref={inputRef}
                      placeholder={placeholder}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        setIsLoaderActive(true);
                      }}
                    />
                  </div>
                )}
              </div>
              {isLoaderActive || filterLoader ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "calc(100% - 146px)" }}>
                  <img src={LoaderImg} alt="Loading" />
                </div>
              ) : !noDataFound ? (
                <div className="d-flex flex-wrap">
                  {filterList.length > 0 && searchedData.length > 0 && !noDataFound && (
                    <div className="col-md-12">
                      <label className="form-check">
                        <input className="form-check-input" type="checkbox" checked={isSelectAllChecked} onChange={() => handleSelectAll()} />
                        <span className="form-check-label">Select All</span>
                      </label>
                    </div>
                  )}
                  {filterData.map((data, i) => (
                    <React.Fragment key={i + data.key}>
                      {!!data.key && data.key !== "" && (
                        <div className="col-md-4">
                          <label className="form-check select_value_input">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={data.key + i}
                              value={data.isChecked}
                              checked={selectedFilterIds.includes(data.key)}
                              onChange={(e) => {
                                handleOnSelect(e.target.checked, data);
                              }}
                              disabled={data.isDisabled === true}
                            />
                            <span className="form-check-label">
                              {!!data.countryCode && data.countryCode !== "" && (
                                <span>
                                  <ReactCountryFlag className="trademo-search-flag mr-2" countryCode={data.countryCode} svg />
                                </span>
                              )}
                              {data?.description ? formatStringCase(data.description, isCapitaliseInViewAll, isTitleCase, false, isParanthesesCapital, isNoTransform) : data?.key ? formatStringCase(data.key, isCapitaliseInViewAll, isTitleCase, false, isParanthesesCapital, isNoTransform) : "Not Available"}
                              {appliedFilterList.includes(data.key) ? <span className="grey-text"> ({commaFormat(filterListDocCount?.[data.key] ?? 0)})</span> : <span className="grey-text"> ({(!!data.doc_count || data.doc_count === 0) && typeof data.doc_count === "number" ? commaFormat(data.doc_count) : data.doc_count ? data.doc_count : ""})</span>}
                            </span>
                          </label>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                  <div ref={lastElementRef} className="d-flex justify-content-center align-items-center mb-2 col-md-12">
                    {isPending || (loader && <img src={LoaderImg} width={48} height={48} alt="Loading..." />)}
                  </div>
                </div>
              ) : (
                noDataFound && <ViewAllFilter content={emptyContent} filterKeyword={filterKeyword} label={label} />
              )}
            </>
          )}
        </Card.Body>
      )}
      {confirmationPopup && (
        <Card.Footer>
          <div className="p-0 m-0 d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary trademo-outline-btn btn-sm mr-2"
              onClick={() => {
                setConfirmationPopup(false);
              }}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm trademo-new-btn"
              onClick={() => {
                setConfirmationPopup(false);
                handleApply();
              }}>
              Confirm
            </button>
          </div>
        </Card.Footer>
      )}
    </Card>
  );
};

export default ViewAll;
